
import { useAuthContext } from 'contexts';
import type { RevalidateTrigger } from 'data/FHIRFetcher';
import HooksPoster from 'data/HooksPoster';
import type { Provenance, Task } from 'fhir/r4';
import { FHIRProxyClient, FHIRUtils, ProvenanceBuilder } from 'fhir-sdoh';
import moment from 'moment';
import React, { useState } from 'react';
import { Button, Confirm } from 'semantic-ui-react';
import * as Helpers from 'utils';

import { ConsultCancelDueDateWarning } from '../../taskHeader/assignTask/consultCancelDueDateWarning';
import {
  applyConsultMemberEventDueDate,
  isConsultationTask,
  isFfsMntConsultationTask,
} from '../../taskHeader/assignTask/hooks/utils';

type CancelTaskProps = {
  patientId: string;
  /*
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. The producer
   * (`taskDetails/index.tsx`'s `patientOrg`) is an `&&` chain over
   * `Patient.managingOrganization.reference`, so it is `undefined` before the
   * patient fetch resolves and for any patient with no managing organization —
   * but `.toUpperCase()` below is unguarded, so cancelling a task for such a
   * patient throws. Declared as it is actually passed; adding a `?? ''` here
   * would put an empty org code into the Provenance instead of failing.
   */
  patientOrganizationId: string | undefined;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  setLoader: React.Dispatch<React.SetStateAction<boolean>>;
  /** When true, Delete Task is disabled so it cannot be clicked again mid-save. */
  loader: boolean;
  taskPayload: Task;
  tasksMutate: RevalidateTrigger;
};

const CancelTask: React.FC<CancelTaskProps> = ({
  patientId,
  patientOrganizationId,
  setErrorMessage,
  setLoader,
  loader,
  taskPayload,
  tasksMutate,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showConsultDueDateWarning, setShowConsultDueDateWarning] = useState(false);

  const { practitionerId, practitionerName, token } = useAuthContext();

  const proxyURL = `${import.meta.env.VITE_API_ENDPOINT}/fhir/`;
  const fhirProxyClient = new FHIRProxyClient(token, proxyURL);
  const requiresFfsMntCancelDueDate = isFfsMntConsultationTask(taskPayload);

  const openConfirmation = () => {
    if (loader) return;
    if (requiresFfsMntCancelDueDate) {
      setShowConsultDueDateWarning(true);
      return;
    }
    setConfirmOpen(true);
  };

  const closeConfirmation = () => {
    setConfirmOpen(false);
  };

  const isInterventionOrEnrollmentCancellation = (task: Task) => {
    const taskHelper = new Helpers.TaskHelper(task);
    const taskCode = taskHelper.getCode();
    return (
      (taskCode?.[0]?.code === 'careplan' || taskCode?.[0]?.code === 'enrollment-action') &&
      (task?.reasonCode?.text === 'intervention' || task?.reasonCode?.text === 'enrollment') &&
      task.status === 'cancelled'
    );
  };

  const updatePatientStatusOnCancellation = async (task: Task) => {
    /*
     * SUSPECTED DEFECT — REPORTED, NOT FIXED. `Task.for` and its `reference` are
     * both `0..1` in FHIR R4, so cancelling a task that is not tied to a patient
     * throws here before the status update is posted.
     */
    const patientId = task.for!.reference!.split('/')[1];
    const payload = {
      patientId,
      status: 'INACTIVE',
      reason: 'DISENROLL',
      taskId: task.id,
    };

    await HooksPoster(`/hooks/patient/status`, token, payload).finally(() => {
      // Passed bare: wrapping it in a void arrow would emit a different callback.
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      setTimeout(tasksMutate, 3000);
    });
  };

  const createAndSendProvenance = async (updatedTask: Task, memberEventDate?: string) => {
    const activity =
      isConsultationTask(updatedTask) && memberEventDate
        ? `A ${updatedTask?.code?.coding?.[0]?.code} Task has been cancelled with due date set to ${memberEventDate}`
        : `A ${updatedTask?.code?.coding?.[0]?.code} Task has been cancelled`;

    const provenance: Provenance = ProvenanceBuilder.build(
      `Task/${updatedTask.id!}`,
      { reference: `Practitioner/${practitionerId}`, display: practitionerName },
      patientId,
      'UPDATE',
      activity,
      { code: patientOrganizationId!.toUpperCase(), display: patientOrganizationId!.toLowerCase() }
    );

    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([updatedTask], provenance);

    try {
      await fhirProxyClient.send('post', '', fhirBundle);
      setTimeout(() => {
        setConfirmOpen(false);
        setShowConsultDueDateWarning(false);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
        tasksMutate();
      }, 3000);
    } catch (err) {
      setLoader(false);
      // The optional calls are the existing guard for a non-axios throw, not added here.
      setErrorMessage(
        (err as { toJSON?: () => { message?: string } })?.toJSON?.()?.message ||
          'Failed to cancel task.',
      );
    }
  };

  const deleteTask = async (memberEventDate?: string) => {
    setLoader(true);

    let updatedTask: Task = { ...taskPayload, status: 'cancelled' };

    if (isFfsMntConsultationTask(updatedTask) && memberEventDate) {
      applyConsultMemberEventDueDate(updatedTask, memberEventDate);
    } else if (isInterventionOrEnrollmentCancellation(updatedTask)) {
      updatedTask.executionPeriod!.end = moment().format('YYYY-MM-DD');
    }

    updatedTask = FHIRUtils.addUpdatedBy(updatedTask, practitionerName);

    if (isInterventionOrEnrollmentCancellation(updatedTask)) {
      await updatePatientStatusOnCancellation(updatedTask);
    }

    await createAndSendProvenance(updatedTask, memberEventDate);
  };

  return (
    <>
      <Button
        size="small"
        floated="right"
        content="Delete Task"
        onClick={openConfirmation}
        negative
        disabled={loader}
        loading={loader}
      />
      <ConsultCancelDueDateWarning
        open={showConsultDueDateWarning && requiresFfsMntCancelDueDate && !loader}
        targetStatus="cancelled"
        onDismiss={() => setShowConsultDueDateWarning(false)}
        onConfirm={(memberEventDate) => {
          setShowConsultDueDateWarning(false);
          void deleteTask(memberEventDate);
        }}
      />
      <Confirm
        open={confirmOpen && !loader}
        onCancel={closeConfirmation}
        onConfirm={() => void deleteTask()}
        content="Cancelling this task will remove it from your list. Are you sure you want to cancel?"
        size="large"
        confirmButton={<Button content="Cancel Task" negative disabled={loader} />}
        cancelButton={<Button content="Nevermind" disabled={loader} />}
      />
    </>
  );
};

export default CancelTask;
